import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { FactList, PageIntro, Section } from '@/components/PageLayout'

/*
 * Operating figures are always shown WITH the date they were measured. A rent
 * or occupancy figure without a date is a claim about today, and it goes stale
 * at the first turnover.
 */

export default function TrackRecordPage() {
  return (
    <div className="bg-background text-foreground">
      <PageIntro title="Track record">
        <p>Every property the firm owns, with its purchase terms and operating figures.</p>
      </PageIntro>

      <section className="border-border border-b">
        <dl className="tabular mx-auto grid max-w-5xl grid-cols-3 px-5 py-10 md:px-8">
          {(
            [
              ['Properties', '1'],
              ['Units', '2'],
              ['Aggregate purchase price', '$170,000'],
            ] as const
          ).map(([k, v], i) => (
            <div key={k} className={i > 0 ? 'border-border border-l pl-5 md:pl-8' : 'pr-5'}>
              <dt className="text-muted-foreground text-sm">{k}</dt>
              <dd className="text-navy mt-1 font-serif text-[clamp(1.6rem,2.5vw+0.8rem,2.4rem)]">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <Section title="West Boulevard duplex">
        <p className="text-fluid-body-lg max-w-2xl">
          A two-unit residential building in the West Boulevard neighborhood on Cleveland&rsquo;s west side, acquired in
          April 2025. The firm&rsquo;s first acquisition.
        </p>

        <div className="mt-10 grid gap-10 lg:grid-cols-2">
          <div>
            <h3 className="text-navy mb-3 font-semibold">Property and purchase</h3>
            <FactList
              rows={[
                ['Type', 'Duplex'],
                ['Building size', '2,348 sq ft'],
                ['Units', '2 (3 bed / 1 bath each)'],
                ['Closed', 'April 2025'],
                ['Purchase price', '$170,000'],
                ['Down payment', '$42,500 (25%)'],
                ['Financing', 'Conventional mortgage'],
                ['Property manager', 'Progressive Urban Property Management'],
              ]}
            />
          </div>
          <div>
            <h3 className="text-navy mb-3 font-semibold">Operating figures, Q2 2025</h3>
            <FactList
              rows={[
                ['Gross monthly rent', '$2,400'],
                ['Rent per unit', '$1,200'],
                ['Occupancy', '2 of 2 units'],
                ['Status', 'Stabilized'],
              ]}
            />
          </div>
        </div>
      </Section>

      <Section title="Lessons from the first acquisition" tinted>
        <dl className="max-w-2xl space-y-7">
          <div>
            <dt className="text-navy mb-1.5 font-semibold">Underwriting margin</dt>
            <dd className="text-foreground/85 leading-relaxed">
              The acquisition budget assumed longer vacancy and higher repair costs than industry averages suggested.
              That margin absorbed the first turnover without putting the property&rsquo;s cash flow at risk.
            </dd>
          </div>
          <div>
            <dt className="text-navy mb-1.5 font-semibold">Professional management</dt>
            <dd className="text-foreground/85 leading-relaxed">
              Delegating leasing and maintenance to a property manager keeps the firm&rsquo;s time on acquisitions,
              oversight, and investor reporting.
            </dd>
          </div>
        </dl>
      </Section>

      <section>
        <div className="mx-auto flex max-w-5xl flex-col gap-6 px-5 py-14 md:flex-row md:items-center md:justify-between md:px-8">
          <p className="text-fluid-h3 text-navy max-w-xl">
            The firm is underwriting its next acquisition. Investor-list members are notified before it closes.
          </p>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/contact">Join the investor list</Link>
          </Button>
        </div>
      </section>
    </div>
  )
}
